// src/adapters/azureAdapter.js
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

/**
 * Azure 语音识别适配器（Web 环境）
 *
 * @param {Object} opts
 * @param {string} opts.region      Azure Speech 资源区域（如 'eastasia' / 'westus'）
 * @param {string} opts.tokenUrl    你后端颁发短期 token 的接口（GET，返回纯文本）
 * @param {string} [opts.lang='zh-CN']   识别语言（'zh-CN' | 'zh-TW' 等）
 * @param {function} [opts.onPartial]    中间结果回调 (text) => void
 * @param {function} [opts.onFinal]      最终结果/结束回调 (text) => void（空串表示“收尾/暂停”）
 * @param {number}   [opts.initialSilenceMs=20000]  首次静默容忍
 * @param {number}   [opts.finalSilenceMs=8000]     说话间歇静默容忍
 * @param {number}   [opts.tokenRefreshMs=8*60*1000] token 刷新周期（默认 8 分钟）
 */
export function createAzureAdapter({
  region,
  tokenUrl,
  lang = 'zh-CN',
  onPartial,
  onFinal,
  initialSilenceMs = 20000,
  finalSilenceMs = 8000,
  tokenRefreshMs = 8 * 60 * 1000,
} = {}) {
  if (!region || !tokenUrl) {
    throw new Error('[azureAdapter] region 与 tokenUrl 必填');
  }

  let recognizer = null;
  let starting = false;
  let stopping = false;
  let refreshTimer = null;

  async function fetchToken() {
    const r = await fetch(tokenUrl, { credentials: 'include' });
    if (!r.ok) throw new Error(`fetch token failed: ${r.status}`);
    return r.text(); // 纯文本 token
  }

  async function makeRecognizer() {
    const token = await fetchToken();

    const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
    speechConfig.speechRecognitionLanguage = lang;

    // 设置静默阈值，避免短停顿就被结束
    speechConfig.setProperty(
      SpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
      String(initialSilenceMs)
    );
    speechConfig.setProperty(
      SpeechSDK.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs,
      String(finalSilenceMs)
    );

    const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
    const r = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

    // 中间结果（边说边出）
    r.recognizing = (_, e) => {
      const t = e?.result?.text?.trim();
      if (t) onPartial?.(t);
    };

    // 最终结果
    r.recognized = (_, e) => {
      const t = e?.result?.text?.trim();
      if (t) onFinal?.(t);
    };

    // 会话中止/取消：给一次“收尾”
    r.canceled = () => { onFinal?.(''); };
    r.sessionStopped = () => { onFinal?.(''); };

    return r;
  }

  function startTokenAutoRefresh() {
    stopTokenAutoRefresh();
    refreshTimer = setInterval(async () => {
      try {
        if (!recognizer) return;
        const t = await fetchToken();
        // JS SDK 支持在运行时更新 token
        recognizer.authorizationToken = t;
      } catch {
        // 忽略网络抖动，下一轮继续
      }
    }, tokenRefreshMs);
  }

  function stopTokenAutoRefresh() {
    if (refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
  }

  return {
    /** 开始连续识别（幂等） */
    async start() {
      if (starting) return;
      starting = true; stopping = false;
      try {
        if (!recognizer) {
          recognizer = await makeRecognizer();
        }
        await new Promise((resolve, reject) =>
          recognizer.startContinuousRecognitionAsync(resolve, err => reject(err))
        );
        startTokenAutoRefresh();
      } finally {
        starting = false;
      }
    },

    /** 停止识别并释放资源（幂等） */
    async stop() {
      if (stopping) return;
      stopping = true;
      try {
        stopTokenAutoRefresh();
        if (recognizer) {
          await new Promise((resolve) =>
            recognizer.stopContinuousRecognitionAsync(() => resolve(), () => resolve())
          );
          recognizer.close();
          recognizer = null;
        }
      } finally {
        stopping = false;
      }
    },
  };
}
